import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { PaymentException } from '../../data/types';
import { date, daysBetween, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { StatCard, StatGrid, StatusChip } from '../../ui/primitives';

const REASONS: Record<PaymentException['reason'], string> = {
  RETURNED_ACCOUNT_CLOSED: 'Returned: account closed',
  RETURNED_INVALID_ACCOUNT: 'Returned: invalid account',
  HELD_UNVERIFIED_BANK: 'Held: bank account not verified',
  HELD_BANK_CHANGE: 'Held: recent bank change',
};

const STATUS_LABEL: Record<PaymentException['status'], string> = {
  OPEN: 'Open',
  AWAITING_INVESTOR: 'Waiting on investor',
  REISSUED: 'Reissued',
  UNCLAIMED: 'Lodged as unclaimed',
};

export default function PaymentExceptionsTab() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [showResolved, setShowResolved] = useState(false);
  const rows = useMemo(
    () =>
      scope.paymentExceptions.filter(
        (item) => showResolved || item.status === 'OPEN' || item.status === 'AWAITING_INVESTOR',
      ),
    [scope.paymentExceptions, showResolved],
  );
  const open = scope.paymentExceptions.filter(
    (item) => item.status === 'OPEN' || item.status === 'AWAITING_INVESTOR',
  );
  const aged = open.filter((item) => daysBetween(item.raisedAt, DEMO_TODAY) > 365);
  const update = (id: string, status: PaymentException['status'], message: string) => {
    dispatch({ type: 'PAYMENT_EXCEPTION_UPDATE', id, status });
    toast(message);
  };

  return (
    <>
      <StatGrid>
        <StatCard
          label="Unpaid amounts"
          value={money(
            sumBy(open, (item) => item.amount),
            true,
          )}
          hint={`${open.length} payments`}
          tone={open.length ? 'warning' : 'neutral'}
        />
        <StatCard
          label="Waiting on investors"
          value={open.filter((item) => item.status === 'AWAITING_INVESTOR').length}
          hint="Task sent to confirm bank details"
        />
        <StatCard
          label="Older than 12 months"
          value={aged.length}
          tone={aged.length ? 'danger' : 'neutral'}
          hint="Candidates for unclaimed money"
        />
      </StatGrid>
      {aged.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {aged.length} payment has been unclaimed for more than 12 months. Lodge it under the
          relevant state unclaimed money law once contact attempts are exhausted.
        </Alert>
      )}
      <Paper variant="outlined">
        <Stack direction="row" spacing={1} sx={{ p: 2 }}>
          <Chip
            label="Open"
            color={!showResolved ? 'primary' : 'default'}
            variant={!showResolved ? 'filled' : 'outlined'}
            onClick={() => setShowResolved(false)}
          />
          <Chip
            label="All"
            color={showResolved ? 'primary' : 'default'}
            variant={showResolved ? 'filled' : 'outlined'}
            onClick={() => setShowResolved(true)}
          />
        </Stack>
        <DataTable
          rows={rows}
          rowKey={(row) => row.id}
          dense
          defaultSort={{ key: 'raised', direction: 'asc' }}
          emptyMessage="No payment exceptions"
          columns={[
            {
              key: 'holder',
              label: 'Holder',
              render: (row) => (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {scope.holderById.get(row.holderId)?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.eventLabel}
                  </Typography>
                </>
              ),
            },
            { key: 'reason', label: 'Reason', render: (row) => REASONS[row.reason] },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              render: (row) => money(row.amount, true),
            },
            {
              key: 'raised',
              label: 'Raised',
              sortValue: (row) => row.raisedAt,
              render: (row) => `${date(row.raisedAt)} (${daysBetween(row.raisedAt, DEMO_TODAY)}d)`,
            },
            {
              key: 'status',
              label: 'Status',
              render: (row) => (
                <StatusChip
                  status={
                    row.status === 'AWAITING_INVESTOR'
                      ? 'PENDING'
                      : row.status === 'OPEN'
                        ? 'OPEN'
                        : 'COMPLETED'
                  }
                  label={STATUS_LABEL[row.status]}
                />
              ),
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (row) =>
                row.status === 'OPEN' || row.status === 'AWAITING_INVESTOR' ? (
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {row.status === 'OPEN' && (
                      <Button
                        size="small"
                        onClick={() =>
                          update(
                            row.id,
                            'AWAITING_INVESTOR',
                            'Task sent to the investor to confirm bank details',
                          )
                        }
                      >
                        Ask investor
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        update(row.id, 'REISSUED', 'Payment reissued in the next ABA file')
                      }
                    >
                      Reissue
                    </Button>
                    {daysBetween(row.raisedAt, DEMO_TODAY) > 365 && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() =>
                          update(row.id, 'UNCLAIMED', 'Recorded for lodgement as unclaimed money')
                        }
                      >
                        Unclaimed
                      </Button>
                    )}
                  </Stack>
                ) : null,
            },
          ]}
        />
      </Paper>
    </>
  );
}
